import { get, set, del, createStore } from 'idb-keyval';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { experimental_createPersister as createPersister } from '@tanstack/query-persist-client-core';
import { defineAppPlugin } from '/app/utils/app';

let store = createStore('bignerve', 'keyval')

const initStore = () => {
    store = createStore('bignerve', 'keyval')
}

const handleError = (err) => {
    if (
      err?.message?.includes('connection is closing') ||
      err?.message?.includes('Internal error opening backing store') ||
      err?.message?.includes('UnknownError') ||
      err?.name === 'UnknownError'
    ) {
      console.warn('IndexedDB error (recovering):', err.message);
      
      // Try to reinitialize the store
      try {
        initStore();
      } catch (reinitError) {
        console.error('Failed to reinitialize IndexedDB store:', reinitError);
        // If reinitialization fails, we'll just continue without persistence
        // This prevents the app from crashing
      }
    } else {
      console.error('IndexedDB error:', err);
      throw err;
    }
  }

// Fallback to Map if IndexedDB fails
const fallbackStorageMap = new Map();

const fallbackStorage = {
  getItem: (key) => {
    try {
      return fallbackStorageMap.get(key);
    } catch (e) {
      console.warn('Fallback storage getItem failed:', e);
      return undefined;
    }
  },
  setItem: (key, value) => {
    try {
      fallbackStorageMap.set(key, value);
    } catch (e) {
      console.warn('Fallback storage setItem failed:', e);
    }
  },
  removeItem: (key) => {
    try {
      fallbackStorageMap.delete(key);
    } catch (e) {
      console.warn('Fallback storage removeItem failed:', e);
    }
  }
};

let useFallback = false;

const safeOperation = (operation) => {
  if (useFallback) {
    return Promise.resolve(undefined);
  }
  
  return operation(store).catch((err) => {
    handleError(err);
    // If we get here, IndexedDB is likely broken, switch to fallback
    if (err?.message?.includes('Internal error opening backing store') || 
        err?.message?.includes('UnknownError') ||
        err?.name === 'UnknownError') {
      useFallback = true;
      console.warn('Switching to localStorage fallback due to IndexedDB issues');
    }
    return Promise.resolve(undefined);
  });
}

const getItem = (key) => {
  if (useFallback) {
    return Promise.resolve(fallbackStorage.getItem(key));
  }
  return safeOperation((store) => get(key, store)).then(result => 
    result !== undefined ? result : fallbackStorage.getItem(key)
  );
}

const setItem = (key, value) => {
  if (useFallback) {
    fallbackStorage.setItem(key, value);
    return Promise.resolve();
  }
  return safeOperation((store) => set(key, value, store)).then(() => {
    // Also store in fallback as backup
    fallbackStorage.setItem(key, value);
  });
}

const removeItem = (key) => {
  if (useFallback) {
    fallbackStorage.removeItem(key);
    return Promise.resolve();
  }
  return safeOperation((store) => del(key, store)).then(() => {
    // Also remove from fallback
    fallbackStorage.removeItem(key);
  });
}

export default defineAppPlugin(({ app }) => {
    const persister = createPersister({
        storage: {
            getItem: fallbackStorage.getItem,
            setItem: fallbackStorage.setItem,
            removeItem: fallbackStorage.removeItem,
        },
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
    })

    app.use(VueQueryPlugin, {
        queryClientConfig: {
            defaultOptions: {
                queries: {
                    persister,
                    gcTime: 1000 * 60 * 60 * 24, // 24 hours
                    staleTime: 1000 * 30,
                    retry: 3,
                },
            },
        },
    })
});
